import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { X, Home, Factory, Calendar, ChevronDown, ChevronRight, FileType2, Building, Upload } from 'lucide-react';
import { valueStreams, office } from '../../data/factories';
import { FileType } from '../../types/media';
import ShinyText from '../UI/ShinyText';

interface SidebarProps {
  isOpen: boolean;
  closeSidebar: () => void;
}

const fileTypes: { id: FileType; name: string }[] = [
  { id: 'shutdown', name: 'Shutdown' },
  { id: 'monthly_meeting', name: 'Réunion mensuelle' },
  { id: 'health_safety', name: 'Santé sécurité' },
  { id: 'vs', name: 'VS' },
  { id: 'general_info', name: 'Information générale' }
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, closeSidebar }) => {
  const [showTypes, setShowTypes] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [expandedVS, setExpandedVS] = useState<string[]>([]);

  const toggleVS = (vsId: string) => {
    setExpandedVS(prev => 
      prev.includes(vsId) 
        ? prev.filter(id => id !== vsId)
        : [...prev, vsId]
    );
  };

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={closeSidebar}
        ></div>
      )}
      
      <aside 
        className={`fixed top-0 left-0 z-30 w-full sm:w-80 md:w-[25%] h-full bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out pt-16 md:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-4 md:hidden flex justify-end">
          <button 
            onClick={closeSidebar}
            className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close sidebar"
          >
            <X size={24} className="text-gray-700 dark:text-gray-200" />
          </button>
        </div>
        
        <nav className="mt-2 h-full overflow-y-auto pb-20">
          <ul className="space-y-1 px-3">
            <li>
              <NavLink 
                to="/"
                className={({ isActive }) => 
                  `flex items-center px-3 sm:px-4 py-3 text-sm rounded-md transition-colors ${
                    isActive 
                      ? 'bg-primary text-white font-medium' 
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
                onClick={closeSidebar}
              >
                <Home size={18} className="mr-3 flex-shrink-0" />
                <ShinyText text="Accueil" />
              </NavLink>
            </li>

            <li>
              <NavLink 
                to="/calendrier"
                className={({ isActive }) => 
                  `flex items-center px-3 sm:px-4 py-3 text-sm rounded-md transition-colors ${
                    isActive 
                      ? 'bg-primary text-white font-medium' 
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
                onClick={closeSidebar}
              >
                <Calendar size={18} className="mr-3 flex-shrink-0" />
                <ShinyText text="Calendrier" />
              </NavLink>
            </li>

            <li>
              <NavLink 
                to="/upload"
                className={({ isActive }) => 
                  `flex items-center px-3 sm:px-4 py-3 text-sm rounded-md transition-colors ${
                    isActive 
                      ? 'bg-primary text-white font-medium' 
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
                onClick={closeSidebar}
              >
                <Upload size={18} className="mr-3 flex-shrink-0" />
                <ShinyText text="Télécharger" />
              </NavLink>
            </li>

            {/* Administration Section */}
            <li className="pt-4">
              <button
                onClick={() => setShowAdmin(!showAdmin)}
                className="w-full flex items-center justify-between px-3 sm:px-4 py-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                <ShinyText text="Administration" />
                {showAdmin ? (
                  <ChevronDown size={16} className="flex-shrink-0" />
                ) : (
                  <ChevronRight size={16} className="flex-shrink-0" />
                )}
              </button>

              <div className={`space-y-1 transition-all duration-300 ${showAdmin ? 'block' : 'hidden'}`}>
                <NavLink 
                  to={`/usine/${office.id}`}
                  className={({ isActive }) => 
                    `flex items-center px-3 sm:px-4 py-3 text-sm rounded-md transition-colors ${
                      isActive 
                        ? 'bg-primary text-white font-medium' 
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`
                  }
                  onClick={closeSidebar}
                >
                  <Building size={18} className="mr-3 flex-shrink-0" />
                  <ShinyText text={office.name} />
                </NavLink>
              </div>
            </li>

            {/* Value Streams Section */}
            {valueStreams.map((vs) => (
              <li key={vs.id} className="pt-4">
                <button
                  onClick={() => toggleVS(vs.id)}
                  className="w-full flex items-center justify-between px-3 sm:px-4 py-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                >
                  <ShinyText text={vs.name} />
                  {expandedVS.includes(vs.id) ? (
                    <ChevronDown size={16} className="flex-shrink-0" />
                  ) : (
                    <ChevronRight size={16} className="flex-shrink-0" />
                  )}
                </button>

                <div className={`space-y-1 transition-all duration-300 ${
                  expandedVS.includes(vs.id) ? 'block' : 'hidden'
                }`}>
                  {vs.factories.map((factory) => (
                    <NavLink 
                      key={factory.id}
                      to={`/usine/${factory.id}`}
                      className={({ isActive }) => 
                        `flex items-center px-3 sm:px-4 py-3 text-sm rounded-md transition-colors ${
                          isActive 
                            ? 'bg-primary text-white font-medium' 
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                      onClick={closeSidebar}
                    >
                      <Factory size={18} className="mr-3 flex-shrink-0" />
                      <ShinyText text={factory.name} />
                    </NavLink>
                  ))}
                </div>
              </li>
            ))}
            
            {/* Types Section - Now at the bottom */}
            <li className="pt-4">
              <button
                onClick={() => setShowTypes(!showTypes)}
                className="w-full flex items-center justify-between px-3 sm:px-4 py-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                <ShinyText text="Types de fichiers" />
                {showTypes ? (
                  <ChevronDown size={16} className="flex-shrink-0" />
                ) : (
                  <ChevronRight size={16} className="flex-shrink-0" />
                )}
              </button>
            </li>
            
            <div className={`space-y-1 transition-all duration-300 ${showTypes ? 'block' : 'hidden'}`}>
              {fileTypes.map((type) => (
                <li key={type.id}>
                  <NavLink 
                    to={`/type/${type.id}`}
                    className={({ isActive }) => 
                      `flex items-center px-3 sm:px-4 py-3 text-sm rounded-md transition-colors ${
                        isActive 
                          ? 'bg-primary text-white font-medium' 
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`
                    }
                    onClick={closeSidebar}
                  >
                    <FileType2 size={18} className="mr-3 flex-shrink-0" />
                    <ShinyText text={type.name} />
                  </NavLink>
                </li>
              ))}
            </div>
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;